import axios from 'axios'
import store from '@/store'
import router from '@/router'

/**
 * Service API pour interagir avec le backend
 */
class ApiService {
    /**
     * Configure le service API
     * @param {String} baseURL URL de base de l'API
     */
    init(baseURL) {
        axios.defaults.baseURL = baseURL
        axios.defaults.headers.common['Accept'] = 'application/json'

        // Intercepteur pour ajouter le token d'authentification
        axios.interceptors.request.use(
            config => {
                const token = localStorage.getItem('auth_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            error => Promise.reject(error)
        )

        // Intercepteur pour gérer les réponses et les erreurs
        axios.interceptors.response.use(
            response => response,
            error => {
                const originalRequest = error.config

                // Gestion des erreurs 401 (non authentifié)
                if (error.response && error.response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true

                    // Si un token existe mais n'est plus valide, déconnexion
                    if (localStorage.getItem('auth_token')) {
                        localStorage.clear();
                        sessionStorage.clear();
                        store.dispatch('auth/logout')
                        router.push({ name: 'login' })
                        return Promise.reject(error)
                    }
                }

                return Promise.reject(error)
            }
        )
    }

    /**
     * Récupérer des données
     * @param {String} resource Chemin de la ressource
     * @param {Object} params Paramètres de requête
     * @returns {Promise} Réponse API
     */
    get(resource, params = {}) {
        return axios.get(resource, {params})
    }

    /**
     * Envoyer des données
     * @param {String} resource Chemin de la ressource
     * @param {Object} data Données à envoyer
     * @returns {Promise} Réponse API
     */
    post(resource, data, options = {}) {
        return axios.post(resource, data, options)
    }

    /**
     * Mettre à jour des données (PUT)
     * @param {String} resource Chemin de la ressource
     * @param {Object} data Données à mettre à jour
     * @returns {Promise} Réponse API
     */
    put(resource, data) {
        return axios.put(resource, data)
    }

    /**
     * Mettre à jour partiellement des données (PATCH)
     * @param {String} resource Chemin de la ressource
     * @param {Object} data Données à mettre à jour
     * @returns {Promise} Réponse API
     */
    patch(resource, data) {
        return axios.patch(resource, data)
    }

    /**
     * Supprimer une ressource
     * @param {String} resource Chemin de la ressource
     * @returns {Promise} Réponse API
     */
    delete(resource) {
        return axios.delete(resource)
    }

    /**
     * Télécharger un fichier vers le serveur
     * @param {String} resource Chemin de la ressource
     * @param {FormData} formData Données du formulaire incluant le fichier
     * @param {Function} progressCallback Fonction de callback pour la progression
     * @returns {Promise} Réponse API
     */
    upload(resource, formData, progressCallback = null) {
        return axios.post(resource, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: progressCallback
                ? progressEvent => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    )
                    progressCallback(percentCompleted)
                }
                : undefined
        })
    }

    /**
     * Télécharger un fichier depuis le serveur
     * @param {String} resource Chemin de la ressource
     * @param {Object} params Paramètres de requête
     * @param {String} filename Nom du fichier à télécharger
     * @returns {Promise} Réponse API
     */
    download(resource, params = {}, filename = '') {
        return axios.get(resource, {
            params,
            responseType: 'blob'
        }).then(response => {
            // response.data est déjà un Blob quand responseType: 'blob'
            const blob = response.data instanceof Blob ? response.data : new Blob([response.data])
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url

            // Utiliser le nom de fichier fourni ou extrait des en-têtes de réponse
            const contentDisposition = response.headers['content-disposition']
            let extractedFilename = ''

            if (contentDisposition) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
                const matches = filenameRegex.exec(contentDisposition)
                if (matches != null && matches[1]) {
                    extractedFilename = matches[1].replace(/['"]/g, '')
                }
            }

            link.setAttribute('download', filename || extractedFilename || 'download')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            
            // Nettoyer l'URL après un délai
            setTimeout(() => window.URL.revokeObjectURL(url), 100)

            return response
        })
    }

    /**
     * Formater les erreurs de l'API
     * @param {Error} error Erreur Axios
     * @returns {Object} Objet d'erreur formaté
     */
    formatError(error) {
        // Erreur de validation (422)
        if (error.response && error.response.status === 422) {
            return {
                status: error.response.status,
                message: error.response.data.message || 'Erreur de validation',
                errors: error.response.data.errors || {}
            }
        }

        // Autres erreurs avec réponse du serveur
        if (error.response && error.response.data) {
            return {
                status: error.response.status,
                message: error.response.data.message || 'Une erreur est survenue',
                data: error.response.data
            }
        }

        // Erreurs réseau ou autres
        return {
            status: error.response ? error.response.status : 0,
            message: error.message || 'Erreur de connexion au serveur'
        }
    }

    /**
     * Obtenir un token d'annulation pour les requêtes
     * @returns {Object} Token d'annulation et fonction d'annulation
     */
    getCancelToken() {
        const CancelToken = axios.CancelToken
        const source = CancelToken.source()

        return {
            cancelToken: source.token,
            cancel: source.cancel
        }
    }
}

export default new ApiService()